import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { theme, card, btnPrimary, btnSecondary, btnDanger, inputStyle } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate, fmtBytes } from "../utils/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { openSafe } from "../utils/safeHref";

// Licensed copies of the organisation's standards (My Services; the consultant's
// client view). The platform ships paraphrase, not the publishers' text: an
// organisation records the licence it bought for each purchased standard,
// uploads the receipt and the copy, and the compliance check then reads the
// organisation's own copy. A consultant can mark the record verified. The
// record is advisory until enforcement is switched on — the API says which
// (`enforcement`), and the panel says so in words.
//
// Every request goes through the API's org scope: the consultant client view
// passes `organizationId`; inside the client shell the read-through query is
// added by the API client itself.

type Requirement = "purchase" | "free_terms" | "open";
type LicenceType = "single_user" | "multi_user" | "network" | "enterprise" | "free_terms";
type LicenceStatus = "attested" | "verified" | "rejected";

interface Store { id: string; label: string; url: string }
interface Publisher { id: string; name: string; stores: Store[] }

interface LicenceFile {
  id: string;
  kind: "copy" | "receipt";
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  textStatus: "none" | "pending" | "ready" | "failed";
  textError: string | null;
  textChars: number | null;
  pages: number | null;
  sectionCount: number | null;
  watermarkText: string | null;
  watermarkLicensee: string | null;
  watermarkReference: string | null;
  watermarkMatch: "matched" | "mismatch" | "not_found" | null;
}

interface Licence {
  id: string;
  publisher: string;
  licenceType: LicenceType;
  licenceReference: string | null;
  licenseeName: string | null;
  seats: number | null;
  purchasedAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  status: LicenceStatus;
  attestedAt: string;
  verifiedAt: string | null;
  verificationNote: string | null;
  files: LicenceFile[];
}

interface StandardRow {
  frameworkId: string;
  label: string;
  requirement: Requirement;
  document: string;
  companions: string[];
  licenceNote: string;
  publisher: Publisher;
  licence: Licence | null;
}

interface Payload {
  enforcement: "advisory" | "required";
  missing: string[];
  viewer: { canAttest: boolean; canVerify: boolean; canDownloadCopies: boolean };
  publishers: Record<string, Publisher>;
  standards: StandardRow[];
}

interface FormState {
  publisher: string;
  licenceType: LicenceType;
  licenceReference: string;
  licenseeName: string;
  seats: string;
  purchasedAt: string;
  expiresAt: string;
  notes: string;
}

const LICENCE_TYPE_LABELS: Record<LicenceType, string> = {
  single_user: "Single-user licence",
  multi_user: "Multi-user licence",
  network: "Network / site licence",
  enterprise: "Enterprise subscription",
  free_terms: "Publisher's free licence terms accepted",
};

const REQUIREMENT_LABELS: Record<Requirement, string> = {
  purchase: "Purchase required",
  free_terms: "Free under publisher terms",
  open: "Open licence",
};

const STATUS_STYLE: Record<LicenceStatus | "none", { label: string; tone: StatusTone }> = {
  none:     { label: "Not recorded", tone: "none" },
  attested: { label: "Attested",     tone: "partial" },
  verified: { label: "Verified",     tone: "met" },
  rejected: { label: "Rejected",     tone: "gap" },
};

const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 2, fontSize: 11, fontWeight: 600, background: bg, color: fg, whiteSpace: "nowrap",
});

const smallBtn: React.CSSProperties = { ...btnSecondary, padding: "5px 10px", fontSize: 12 };
const smallPrimary: React.CSSProperties = { ...btnPrimary, padding: "5px 10px", fontSize: 12 };
const smallDanger: React.CSSProperties = { ...btnDanger, padding: "5px 10px", fontSize: 12 };
const label: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, marginBottom: 3, display: "block" };

function isoDay(d: string | null): string { return d ? d.slice(0, 10) : ""; }

function formFor(row: StandardRow): FormState {
  const l = row.licence;
  return {
    publisher: l?.publisher ?? row.publisher.id,
    licenceType: l?.licenceType ?? (row.requirement === "free_terms" ? "free_terms" : "single_user"),
    licenceReference: l?.licenceReference ?? "",
    licenseeName: l?.licenseeName ?? "",
    seats: l?.seats ? String(l.seats) : "",
    purchasedAt: isoDay(l?.purchasedAt ?? null),
    expiresAt: isoDay(l?.expiresAt ?? null),
    notes: l?.notes ?? "",
  };
}

function describeLicence(l: Licence, publishers: Record<string, Publisher>): string {
  const parts = [
    LICENCE_TYPE_LABELS[l.licenceType],
    l.seats ? `${l.seats} seats` : null,
    `from ${publishers[l.publisher]?.name ?? l.publisher}`,
    l.licenceReference ? `ref ${l.licenceReference}` : null,
    l.licenseeName ? `licensed to ${l.licenseeName}` : null,
    l.purchasedAt ? `bought ${fmtDateOnly(l.purchasedAt)}` : null,
    l.expiresAt ? `expires ${fmtDateOnly(l.expiresAt)}` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

function watermarkChip(f: LicenceFile): React.ReactNode {
  if (f.textStatus === "pending") return <span style={chip(theme.color.panelAlt, theme.color.subtle)}>Reading the copy…</span>;
  if (f.textStatus === "failed") return <span style={chip(theme.color.dangerBg, theme.color.dangerText)}>Could not read</span>;
  if (f.textStatus !== "ready") return null;
  if (f.watermarkMatch === "matched") return <span style={chip(theme.color.successBg, theme.color.successText)}>Licensed to {f.watermarkLicensee} — matches</span>;
  if (f.watermarkMatch === "mismatch") return <span style={chip(theme.color.dangerBg, theme.color.dangerText)}>Licensed to {f.watermarkLicensee} — not this organisation</span>;
  return <span style={chip(theme.color.warningBg, theme.color.warningText)}>No publisher watermark found</span>;
}

export function StandardLicencesPanel({ organizationId }: { organizationId?: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [verifyNote, setVerifyNote] = useState<Record<string, string>>({});
  const [uploadTarget, setUploadTarget] = useState<{ frameworkId: string; kind: "copy" | "receipt" } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = organizationId ? { organizationId } : undefined;

  const load = useCallback(async () => {
    try {
      setData(await api.get<Payload>("/standard-licences", query));
      setError("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the licence records."));
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => { void load(); }, [load]);

  // A copy being read on the worker: poll until it settles.
  useEffect(() => {
    if (!data?.standards.some((s) => s.licence?.files.some((f) => f.textStatus === "pending"))) return;
    const t = setInterval(() => { void load(); }, 5000);
    return () => clearInterval(t);
  }, [data, load]);

  if (!loaded) return null;
  if (!data && error) return <div style={{ ...card, marginBottom: 20, color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!data || data.standards.length === 0) return null;

  const { viewer, publishers } = data;
  const recordable = data.standards.filter((s) => s.requirement !== "open");
  const open = data.standards.filter((s) => s.requirement === "open");

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "That did not work — try again."));
    } finally {
      setBusy(null);
    }
  };

  const startEdit = (row: StandardRow) => { setEditing(row.frameworkId); setForm(formFor(row)); setError(""); };

  const save = (row: StandardRow) => run(`save:${row.frameworkId}`, async () => {
    if (!form) return;
    await api.put(`/standard-licences/${row.frameworkId}`, {
      publisher: form.publisher,
      licenceType: form.licenceType,
      licenceReference: form.licenceReference.trim() || null,
      licenseeName: form.licenseeName.trim() || null,
      seats: form.licenceType === "multi_user" && form.seats.trim() ? Number(form.seats) : null,
      purchasedAt: form.purchasedAt || null,
      expiresAt: form.expiresAt || null,
      notes: form.notes.trim() || null,
    }, query);
    setEditing(null);
    setForm(null);
  });

  const remove = (row: StandardRow) => {
    if (!window.confirm(`Remove the licence record for ${row.label} and every file on it?`)) return;
    void run(`remove:${row.frameworkId}`, async () => { await api.del(`/standard-licences/${row.frameworkId}`, query); });
  };

  const verify = (row: StandardRow, decision: "verified" | "rejected") => run(`verify:${row.frameworkId}`, async () => {
    await api.post(`/standard-licences/${row.frameworkId}/verify`, { decision, note: verifyNote[row.frameworkId]?.trim() || null }, query);
    setVerifyNote((n) => ({ ...n, [row.frameworkId]: "" }));
  });

  const pickFile = (frameworkId: string, kind: "copy" | "receipt") => {
    setUploadTarget({ frameworkId, kind });
    if (inputRef.current) {
      inputRef.current.accept = kind === "copy" ? ".pdf" : ".pdf,.png,.jpg,.jpeg,.webp";
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const upload = async (file: File) => {
    const target = uploadTarget;
    setUploadTarget(null);
    if (!target) return;
    await run(`upload:${target.frameworkId}`, async () => {
      const contentType = file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream");
      const { blobPath, uploadUrl, maxBytes } = await api.post<{ blobPath: string; uploadUrl: string; maxBytes: number }>(
        `/standard-licences/${target.frameworkId}/files/request-upload`, { kind: target.kind, fileName: file.name, contentType }, query,
      );
      if (file.size > maxBytes) throw new Error(`Files must be under ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      const put = await fetch(uploadUrl, { method: "PUT", headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType }, body: file }).catch(() => null);
      if (!put?.ok) throw new Error("The upload to storage failed — check your connection and try again.");
      await api.post(`/standard-licences/${target.frameworkId}/files`, { kind: target.kind, fileName: file.name, blobPath }, query);
    });
  };

  const removeFile = (row: StandardRow, f: LicenceFile) => {
    // The API returns a verified licence to "attested" when its files change: what was verified is no longer what is on file.
    const reverify = row.licence?.status === "verified" ? " The licence goes back to attested until a consultant verifies it again." : "";
    if (!window.confirm(`Remove ${f.fileName}?${reverify}`)) return;
    void run(`file:${f.id}`, async () => { await api.del(`/standard-licences/${row.frameworkId}/files/${f.id}`, query); });
  };

  const download = (row: StandardRow, f: LicenceFile) => run(`file:${f.id}`, async () => {
    const { url } = await api.get<{ url: string }>(`/standard-licences/${row.frameworkId}/files/${f.id}/download-url`, query);
    openSafe(url);
  });

  const reprocess = (row: StandardRow, f: LicenceFile) => run(`file:${f.id}`, async () => {
    await api.post(`/standard-licences/${row.frameworkId}/files/${f.id}/reprocess`, undefined, query);
  });

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <input ref={inputRef} type="file" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Licensed Copies of Your Standards</div>
        <span style={chip(data.enforcement === "required" ? theme.color.warningBg : theme.color.panelAlt, data.enforcement === "required" ? theme.color.warningText : theme.color.subtle)}>
          {data.enforcement === "required" ? "Required for AI checks" : "Advisory — nothing is blocked"}
        </span>
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
        The platform paraphrases each standard; the publisher's own text is licensed per organisation. Record the licence you hold
        for each purchased standard and upload your copy: the compliance check then reads your copy's wording for every control it
        covers, your consultant can confirm you hold it, and the copy itself is never shown to anyone outside your organisation.
        {data.enforcement === "advisory" && " Recording a licence is optional at present."}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {recordable.map((row) => {
          const l = row.licence;
          const status = STATUS_STYLE[l?.status ?? "none"];
          const isEditing = editing === row.frameworkId && form;
          const copies = l?.files.filter((f) => f.kind === "copy") ?? [];
          const receipts = l?.files.filter((f) => f.kind === "receipt") ?? [];
          return (
            <div key={row.frameworkId} style={{ padding: "12px 14px", background: theme.color.panelAlt, border: `1px solid ${l ? theme.color.accent : theme.color.border}`, borderRadius: 3 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, flex: 1, minWidth: 180 }}>{row.label}</span>
                <span style={chip(theme.color.panel, theme.color.subtle)}>{REQUIREMENT_LABELS[row.requirement]}</span>
                <StatusTag tone={status.tone}>{status.label}</StatusTag>
              </div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4 }}>
                {row.document}{row.companions.length > 0 && ` (a copy of ${row.companions.join(" or ")} counts too)`} — {row.publisher.name}.
                {row.publisher.stores.length > 0 && (
                  <>
                    {" "}
                    {row.publisher.stores.map((s, i) => (
                      <span key={s.id}>
                        {i > 0 && " · "}
                        <button type="button" onClick={() => openSafe(s.url)} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>{s.label}</button>
                      </span>
                    ))}
                  </>
                )}
              </div>
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>{row.licenceNote}</div>

              {l && !isEditing && (
                <div style={{ marginTop: 10, fontSize: 12, color: theme.color.text }}>
                  <div>{describeLicence(l, publishers)}</div>
                  <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                    Attested {fmtDate(l.attestedAt)}
                    {l.status === "verified" && l.verifiedAt && ` · verified ${fmtDate(l.verifiedAt)}`}
                    {l.status === "rejected" && l.verifiedAt && ` · rejected ${fmtDate(l.verifiedAt)}`}
                    {l.verificationNote && ` — “${l.verificationNote}”`}
                  </div>
                  {l.notes && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4, whiteSpace: "pre-wrap" }}>{l.notes}</div>}
                </div>
              )}

              {l && (copies.length > 0 || receipts.length > 0) && !isEditing && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {[...copies, ...receipts].map((f) => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, padding: "6px 10px", background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
                      <span style={chip(theme.color.panelAlt, theme.color.subtle)}>{f.kind === "copy" ? "Copy" : "Receipt"}</span>
                      <span style={{ color: theme.color.text, flex: 1, minWidth: 160 }}>{f.fileName} <span style={{ color: theme.color.faint }}>({fmtBytes(f.sizeBytes)}{f.pages ? `, ${f.pages} pages` : ""})</span></span>
                      {f.kind === "copy" && watermarkChip(f)}
                      {f.kind === "copy" && f.textStatus === "ready" && (
                        <span style={{ fontSize: 11, color: theme.color.faint }}>{f.sectionCount ?? 0} clauses indexed</span>
                      )}
                      {f.kind === "copy" && f.textStatus === "failed" && f.textError && <span style={{ fontSize: 11, color: theme.color.dangerText }}>{f.textError}</span>}
                      {(f.kind === "receipt" || viewer.canDownloadCopies) && (
                        <button type="button" style={smallBtn} disabled={busy === `file:${f.id}`} onClick={() => void download(row, f)}>Download</button>
                      )}
                      {viewer.canAttest && f.kind === "copy" && f.textStatus === "failed" && (
                        <button type="button" style={smallBtn} disabled={busy === `file:${f.id}`} onClick={() => void reprocess(row, f)}>Try again</button>
                      )}
                      {viewer.canAttest && (
                        <button type="button" style={smallDanger} disabled={busy === `file:${f.id}`} onClick={() => removeFile(row, f)}>Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {isEditing && form && (
                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                  <div>
                    <span style={label}>Publisher / store</span>
                    <select style={inputStyle} value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })}>
                      {Object.values(publishers).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <span style={label}>Licence</span>
                    <select style={inputStyle} value={form.licenceType} onChange={(e) => setForm({ ...form, licenceType: e.target.value as LicenceType })}>
                      {(row.requirement === "free_terms" ? (["free_terms"] as LicenceType[]) : (["single_user", "multi_user", "network", "enterprise"] as LicenceType[])).map((t) => (
                        <option key={t} value={t}>{LICENCE_TYPE_LABELS[t]}</option>
                      ))}
                    </select>
                  </div>
                  {row.requirement === "purchase" && (
                    <>
                      <div>
                        <span style={label}>Order / licence reference</span>
                        <input style={inputStyle} value={form.licenceReference} maxLength={120} placeholder="OP-123456" onChange={(e) => setForm({ ...form, licenceReference: e.target.value })} />
                      </div>
                      <div>
                        <span style={label}>Licensed to (as printed)</span>
                        <input style={inputStyle} value={form.licenseeName} maxLength={200} onChange={(e) => setForm({ ...form, licenseeName: e.target.value })} />
                      </div>
                      {form.licenceType === "multi_user" && (
                        <div>
                          <span style={label}>Seats</span>
                          <input style={inputStyle} type="number" min={1} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} />
                        </div>
                      )}
                      <div>
                        <span style={label}>Purchased</span>
                        <input style={inputStyle} type="date" value={form.purchasedAt} onChange={(e) => setForm({ ...form, purchasedAt: e.target.value })} />
                      </div>
                      <div>
                        <span style={label}>Expires (subscriptions)</span>
                        <input style={inputStyle} type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                      </div>
                    </>
                  )}
                  {row.requirement === "free_terms" && (
                    <div>
                      <span style={label}>Accepted on</span>
                      <input style={inputStyle} type="date" value={form.purchasedAt} onChange={(e) => setForm({ ...form, purchasedAt: e.target.value })} />
                    </div>
                  )}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span style={label}>Notes</span>
                    <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={form.notes} maxLength={2000} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
                    <button type="button" style={smallPrimary} disabled={busy === `save:${row.frameworkId}`} onClick={() => void save(row)}>
                      {busy === `save:${row.frameworkId}` ? "Saving…" : l ? "Save changes" : "Record licence"}
                    </button>
                    <button type="button" style={smallBtn} onClick={() => { setEditing(null); setForm(null); }}>Cancel</button>
                  </div>
                </div>
              )}

              {!isEditing && (viewer.canAttest || (viewer.canVerify && l)) && (
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {viewer.canAttest && (
                    <button type="button" style={l ? smallBtn : smallPrimary} onClick={() => startEdit(row)}>{l ? "Edit record" : row.requirement === "free_terms" ? "Record acceptance" : "Record licence"}</button>
                  )}
                  {viewer.canAttest && l && row.requirement === "purchase" && (
                    <button type="button" style={smallBtn} disabled={busy === `upload:${row.frameworkId}`} onClick={() => pickFile(row.frameworkId, "copy")}>
                      {busy === `upload:${row.frameworkId}` ? "Uploading…" : "Upload your copy (PDF)"}
                    </button>
                  )}
                  {viewer.canAttest && l && row.requirement === "purchase" && (
                    <button type="button" style={smallBtn} disabled={busy === `upload:${row.frameworkId}`} onClick={() => pickFile(row.frameworkId, "receipt")}>Upload receipt</button>
                  )}
                  {viewer.canAttest && l && (
                    <button type="button" style={smallDanger} disabled={busy === `remove:${row.frameworkId}`} onClick={() => remove(row)}>Remove record</button>
                  )}
                  {viewer.canVerify && l && (
                    <span style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: "auto" }}>
                      <input
                        style={{ ...inputStyle, width: 220, padding: "5px 8px", fontSize: 12 }}
                        placeholder="Verification note (optional)"
                        value={verifyNote[row.frameworkId] ?? ""}
                        maxLength={1000}
                        onChange={(e) => setVerifyNote((n) => ({ ...n, [row.frameworkId]: e.target.value }))}
                      />
                      {l.status !== "verified" && (
                        <button type="button" style={smallPrimary} disabled={busy === `verify:${row.frameworkId}`} onClick={() => void verify(row, "verified")}>Mark verified</button>
                      )}
                      {l.status !== "rejected" && (
                        <button type="button" style={smallDanger} disabled={busy === `verify:${row.frameworkId}`} onClick={() => void verify(row, "rejected")}>Reject</button>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {open.length > 0 && (
          <div style={{ fontSize: 12, color: theme.color.subtle, padding: "8px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
            <strong style={{ color: theme.color.text }}>Nothing to record:</strong>{" "}
            {open.map((s) => `${s.label} (${s.licenceNote.replace(/\.$/, "")})`).join("; ")}.
          </div>
        )}
      </div>
    </div>
  );
}
